import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Stack, 
  Typography,
  Skeleton
} from '@compass/core';
import { Select } from 'components';
import { StudentDataProvider, useStudentData } from '../contexts/StudentDataContext';
import { DashboardCard } from "../components/DashboardCard";

const TotalStudentsCardContent: React.FC = () => {
  const { data, loading, error } = useStudentData();

  if (loading) {
    return <Skeleton variant="rounded" height={300} />;
  }

  if (error) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!data || data.rows.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>
           {data?.schoolYear 
              ? `No enrollments found for ${data.schoolYear}` 
              : 'No enrollment data available'}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* School Year Header */}
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{ mb: 2, pb: 1, borderBottom: '2px solid', borderColor: 'divider' }}
      >
        <Typography variant="subtitle1" fontWeight="bold">Grade</Typography>
        <Typography variant="subtitle1" fontWeight="bold">{data.schoolYear}</Typography>
      </Stack>

      {/* Grade Rows */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        <Stack direction="column">
          {data.rows.map((row) => (
            <Stack 
              key={row.grade}
              direction="row" 
              justifyContent="space-between" 
              alignItems="center"
              sx={{ 
                py: 1, 
                px: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Typography sx={{ color: 'primary.main', fontWeight: 'medium' }}>
                {row.grade}
              </Typography>
              <Typography>{row.count.toLocaleString()}</Typography>
            </Stack>
          ))}
        </Stack>
      </Box>
      <Stack 
        direction="row" 
        justifyContent="space-between" 
        alignItems="center"
        sx={{ 
          py: 1.5,
          px: 1,
          mt: 2,
          borderTop: '2px solid',
          borderColor: 'primary.main',
          backgroundColor: 'rgba(49, 130, 189, 0.15)',
          borderRadius: '4px'
        }}
      >
        <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
          Total
        </Typography>
        <Typography sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
          {data.total.toLocaleString()}
        </Typography>
      </Stack>

      {/* Footer Label */}
      <Box sx={{ mt: 2, textAlign: 'center' }}>
        <Typography variant="caption" color="text.secondary">
          Grade Levels Served
        </Typography>
      </Box>
    </Box>
  );
};

// Helper type for year options
interface YearOption {
  value: number;
  label: string;
}

export const TotalStudentsCard: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState<YearOption | null>(null);
  const [availableYears, setAvailableYears] = useState<YearOption[]>([]);

  // Initialize years on mount
  useEffect(() => {
    const currentYear = new Date().getFullYear();
    
    const years: YearOption[] = [];
    for (let i = -5; i <= 0; i++) {
      const year = currentYear + i;
      years.push({
        value: year,
        label: `${year}-${(year + 1).toString().slice(2)}`
      });
    }
    
    const defaultYear = years.find(y => y.value === currentYear);
    
    setAvailableYears(years);
    setSelectedYear(defaultYear || years[5]);
  }, []);

  const handleYearChange = (event: any, value: YearOption | null) => {
    if (value) {
      setSelectedYear(value);
    }
  };

  // Don't render until we have a selected year
  if (selectedYear === null) {
    return (
        <DashboardCard title="Total Students">
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rounded" height={300} />
            </Box>
        </DashboardCard >
    );
  }

  return (
      <DashboardCard title="Total Students">
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Stack direction="row" spacing="sm" alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 'fit-content', mr: 2 }}>
                School Year
              </Typography>
              <Box sx={{ width:240 }}>
                <Select
                  value={selectedYear}
                  options={availableYears}
                   getOptionLabel={(option: YearOption) => {
                    const currentYear = new Date().getFullYear();
                    return option.value === currentYear 
                      ? `${option.label} (Current)` 
                      : option.label;
                  }}
                  onChange={handleYearChange}
                  size="small"
                />
              </Box>
            </Stack>
            <Box sx={{ flexGrow: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <StudentDataProvider schoolYear={selectedYear.value}>
                <TotalStudentsCardContent />
              </StudentDataProvider>
            </Box>
          </Box>
      </DashboardCard>
  );
};